/**
 * @file UpgradeDialog.tsx
 *
 * @summary App-wide upgrade dialog, opened from anywhere.
 *
 * Built on a Base UI dialog handle, so it needs no modal manager:
 *
 *   - `<UpgradeDialog />` is mounted once, in the router's `Root`.
 *   - `openUpgradeDialog(message)` opens it, inside or outside React.
 *
 * Each open passes its own message as the payload, so one caller's message
 * never leaks into the next.
 */

import { Alert, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AlertCircleIcon, CheckIcon, ZapIcon } from 'lucide-react'

import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'

import { ENVIRONMENT } from '@/shared/environment'

type UpgradeDialogPayload = {
  message?: string
}

const DEFAULT_MESSAGE = 'Upgrade to unlock premium features.'

const FEATURES = [
  'Add more rundowns and events to your account.',
  'Create unlimited cues and columns per rundown.',
  'Invite more team members for collaboration.',
  'Export rundowns in PDF and CSV formats.',
  'Control rundowns through our API.',
  'Upload files and media up to 1GB in rundown cells.',
]

const upgradeDialog = DialogPrimitive.createHandle<UpgradeDialogPayload>()

/**
 * Open the upgrade dialog with a message saying which limit was hit.
 *
 * @remarks
 *   A plain function, safe in event handlers and non-React code. Base UI
 *   ignores it while `<UpgradeDialog />` is not mounted.
 */
export function openUpgradeDialog(message?: string) {
  upgradeDialog.openWithPayload({ message })
}

/**
 * App-wide upgrade dialog. Mount exactly once, in the router's `Root`.
 */
export function UpgradeDialog() {
  return (
    <Dialog handle={upgradeDialog}>
      {({ payload }) => (
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Time to upgrade</DialogTitle>
          </DialogHeader>

          <Alert className="max-w-full">
            <AlertCircleIcon />
            <AlertTitle>{payload?.message || DEFAULT_MESSAGE}</AlertTitle>
          </Alert>

          <div className="flex flex-col gap-1 px-1.5 text-muted-foreground">
            <p>With paid plans, you can:</p>

            <ul className="flex flex-col gap-1 py-1.5">
              {FEATURES.map((feature) => (
                <li
                  key={feature}
                  className="flex items-center gap-1.5"
                >
                  <CheckIcon className="size-3 flex-none text-green-400" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          <DialogFooter>
            <DialogClose
              nativeButton={false}
              render={
                <Button
                  nativeButton={false}
                  render={
                    <a
                      href={ENVIRONMENT.marketingLinks.pricing}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  }
                />
              }
            >
              <ZapIcon />
              Upgrade my account
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  )
}
